import React from "react";
import { ContentSection, getFileExtension } from "sprint-common-web";

import { CloudVideoAssetItem } from "../../../../../../../common/api/master-api/assets/get-cloud-video-assets/GetCloudVideoAssetsResponse";
import { useAssetsApi } from "../../../../../../../common/api/master-api/assets/useAssetsApi";
import { GetCloudFilePreviewRequest } from "../../../../../../../common/api/master-api/cloud-files/get-preview/GetCloudFilePreviewRequest";
import { useCloudFileApi } from "../../../../../../../common/api/master-api/cloud-files/useCloudFileApi";
import { RecordType } from "../../../../../../../common/types/RecordType";
import { AppConfigContext } from "../../../../../../../core/configuration/Configuration";
import { AuthContext } from "../../../../../../../core/security/AuthProvider";
import { isDvbFile, isZipFile } from "../../../../../../common/isZipDvbFile";
import { CloudFileDetails } from "../../../../CloudFileDetails";
import { useCloudFileDetails } from "../../../../common/useCloudFileDetails";
import {
    CloudFilePreviewItem,
    mapToCloudFilePreviewItem,
    setCloudFrontAuthCookie
} from "../types/CloudFilePreviewItem";
import { CloudFilePreviewContext } from "./CloudFilePreviewContext";

export const CloudFilePreviewProvider: React.FC = ({ children }) => {
    const [filePreview, setFilePreview] = React.useState<CloudFilePreviewItem>();
    const [isPreviewLoading, setIsPreviewLoading] = React.useState<boolean>(true);
    const [availableVideoFiles, setAvailableVideoFiles] = React.useState<CloudVideoAssetItem[]>([]);
    const [selectedVideoCloudFileId, setSelectedVideoCloudFileId] = React.useState<string | undefined>();

    const { data: cloudFileDetails, isLoading: isCloudFileDetailsLoading } = useCloudFileDetails();

    const { getCloudFilePreview } = useCloudFileApi();
    const { getCloudVideoAssets } = useAssetsApi();
    const { sprintUser } = React.useContext(AuthContext);
    const { masterConfig } = React.useContext(AppConfigContext);

    const { isDvb, isZip } = getFromCloudFilePath(cloudFileDetails);
    const isPreviewAvailable = !isDvb;
    const reloadWithConformingVideo = !isZip;

    const reloadPreview = async (request: GetCloudFilePreviewRequest) => {
        setCloudFrontAuthCookie(masterConfig.cloudFrontDomain, sprintUser.accessToken);
        setIsPreviewLoading(true);

        const isVideoRecord = cloudFileDetails?.recordType === RecordType.Video;
        const videoCloudFileId = isVideoRecord
            ? request.videoCloudFileId
            : request.videoCloudFileId ?? (await getDefaultVideoCloudFileId(request.versionIds));
        setSelectedVideoCloudFileId(videoCloudFileId);

        try {
            const response = await getCloudFilePreview({ ...request, videoCloudFileId });
            setFilePreview(mapToCloudFilePreviewItem(response));
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const getDefaultVideoCloudFileId = async (versionIds?: string[]): Promise<string | undefined> => {
        if (!versionIds?.length) {
            setAvailableVideoFiles([]);
            return undefined;
        }

        const { assets } = await getCloudVideoAssets({ contentVersionIds: versionIds });
        setAvailableVideoFiles(assets);

        return assets[0]?.cloudFileId;
    };

    if (!cloudFileDetails) return <ContentSection header="Preview" isLoading={isCloudFileDetailsLoading} />;

    return (
        <CloudFilePreviewContext.Provider
            value={{
                isPreviewLoading,
                filePreview,
                reloadPreview,
                availableVideoFiles,
                selectedVideoCloudFileId,
                isDvb,
                isZip,
                isPreviewAvailable,
                reloadWithConformingVideo,
                cloudFileDetails,
                isCloudFileDetailsLoading
            }}
        >
            {children}
        </CloudFilePreviewContext.Provider>
    );
};

const getFromCloudFilePath = (cloudFile: CloudFileDetails | undefined) => {
    let isDvb = false,
        isZip = false;

    if (cloudFile) {
        const extension = getFileExtension(cloudFile.filePath);

        isZip = isZipFile(extension);
        isDvb = isDvbFile(extension);
    }
    return { isZip, isDvb };
};
